import { collection, doc, documentId, getDoc, getDocFromCache, getDocs, query, where, runTransaction, serverTimestamp } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '@/firebaseConfig'
import { defaultTimesheet, entryChanges, mergeEdits, totalHours, validateEntries } from '@/utils/timesheetEditor.cjs'
import { createReadCache, nearbyMonths } from '@/utils/readCache.cjs'

const sheets = createReadCache()
const projects = createReadCache({ max: 80 })
const holidays = createReadCache({ freshFor: 5 * 60 * 1000 })
let cacheUser = auth.currentUser?.uid
onAuthStateChanged(auth, user => {
  if (cacheUser !== user?.uid) { sheets.clear(); projects.clear(); holidays.clear() }
  cacheUser = user?.uid
})
const key = (userId, projectId, monthId) => `${userId}/${projectId}/${monthId}`

export function rememberProjects(userId, data) {
  data.forEach(project => projects.seed(`${userId}/${project.id}`, project))
}

export async function readProject(userId, projectId, onCached) {
  const ref = doc(db, 'USERS', userId, 'projects', projectId)
  // Dashboard queries already contain the project. Show that copy immediately,
  // while independently checking for changed settings or a deleted project.
  const cached = projects.peek(`${userId}/${projectId}`)
  if (cached) onCached?.(cached)
  const fresh = getDoc(ref)
  let settled = false
  if (!cached) getDocFromCache(ref).then(snapshot => {
    if (!settled && snapshot.exists()) onCached?.(snapshot.data())
  }).catch(() => {})
  const snapshot = await fresh.finally(() => { settled = true })
  if (!snapshot.exists()) {
    projects.remove(`${userId}/${projectId}`)
    throw new Error('This project could not be found.')
  }
  const data = snapshot.data()
  if (auth.currentUser?.uid === userId) projects.seed(`${userId}/${projectId}`, data)
  return data
}

export function cachedTimesheet(userId, projectId, monthId) {
  return sheets.peek(key(userId, projectId, monthId))
}

function readHolidays(country, monthId) {
  if (!country) return Promise.resolve([])
  return holidays.load(`${country}/${monthId}`, async () => {
    const snapshot = await getDoc(doc(db, 'PublicHolidays', country, 'Years', monthId.slice(0, 4), 'Months', monthId.slice(4, 6)))
    return snapshot.data()?.holidays || []
  })
}

export function timesheetRef(userId, projectId, monthId) {
  return doc(db, 'USERS', userId, 'projects', projectId, 'timesheets', monthId)
}

export function readTimesheet(userId, projectId, monthId, projectPromise, { force = false } = {}) {
  const ids = nearbyMonths(monthId)
  const needed = ids.filter(id => !sheets.pending(key(userId, projectId, id)) && (force && id === monthId || !sheets.fresh(key(userId, projectId, id))))
  // One round trip also warms the previous two months and next month. The
  // selected month's result does not wait for missing neighbours/holidays.
  let batch
  const fetchBatch = () => {
    if (!batch) batch = getDocs(query(collection(db, 'USERS', userId, 'projects', projectId, 'timesheets'), where(documentId(), 'in', needed)))
      .then(snapshot => ({ documents: new Map(snapshot.docs.map(document => [document.id, document.data()])), fromCache: snapshot.metadata.fromCache }))
    return batch
  }
  const requests = ids.map(id => sheets.load(key(userId, projectId, id), async () => {
    // A freshness boundary can pass between selecting the batch and starting
    // this read. Never interpret a document omitted from the query as missing.
    const result = needed.includes(id) ? await fetchBatch() : await getDoc(timesheetRef(userId, projectId, id)).then(snapshot => ({
      documents: new Map(snapshot.exists() ? [[id, snapshot.data()]] : []), fromCache: snapshot.metadata.fromCache
    }))
    const data = result.documents.get(id)
    if (data) {
      if (!Array.isArray(data.entries)) throw new Error('This month has no editable entries. Please check the timesheet data.')
      return { exists: true, data }
    }
    if (result.fromCache) throw new Error('Reconnect to check whether this month already has saved hours.')
    const project = await projectPromise
    const days = await readHolidays(project.projectDetails?.holidayCalendar, id)
    // Preloading must never create a timesheet or generate/email a document.
    return { exists: false, data: defaultTimesheet(project, id, days) }
  }, { force: force && id === monthId }))
  // Failed speculative reads must not block or reject the requested month.
  requests.slice(1).forEach(request => request.catch(() => {}))
  return requests[0]
}

export function prefetchProjectTimesheets(userId, project, monthId) {
  if (auth.currentUser?.uid !== userId) return Promise.resolve()
  return readTimesheet(userId, project.id, monthId, Promise.resolve(project)).catch(() => {})
}

export async function saveTimesheet(userId, projectId, monthId, sheet) {
  const ref = timesheetRef(userId, projectId, monthId)
  const edited = sheet.entries.map(entry => ({ ...entry }))
  validateEntries(edited, monthId)
  const data = await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref)
    if (!snapshot.exists()) {
      if (sheet.exists) throw new Error('This timesheet was removed elsewhere. Reload this month before saving.')
      const entries = mergeEdits(sheet.baseline, sheet.baseline, edited, monthId)
      const data = { startDate: sheet.startDate, endDate: sheet.endDate, entries, totalHours: totalHours(entries) }
      transaction.set(ref, { ...data, updatedAt: serverTimestamp() })
      return data
    }
    const latest = snapshot.data()
    if (!Array.isArray(latest.entries)) throw new Error('This month has no editable entries. Reload it before saving.')
    const entries = mergeEdits(latest.entries, sheet.baseline, edited, monthId)
    const data = { ...latest, entries, totalHours: totalHours(entries) }
    if (entryChanges(sheet.baseline, edited).length) {
      // Keep generated files, invoice numbers and other metadata untouched.
      transaction.update(ref, { entries, totalHours: data.totalHours, updatedAt: serverTimestamp() })
    }
    return data
  })
  // Replace the cached baseline only after the server commits successfully.
  sheets.seed(key(userId, projectId, monthId), { exists: true, data })
  return data
}
